const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 2000;
const REQUEST_TIMEOUT_MS = 300_000;
const STREAM_TIMEOUT_MS = 660_000;
const STREAM_STALL_MS = 180_000;
const STREAM_MAX_RETRIES = 2;
const STREAM_RETRY_BASE_MS = 2000;

export interface EdgeFunctionRequest {
  action: 'generate' | 'review' | 'rewrite' | 'tenancy-review-rewrite' | 'commercial-lease-review';
  systemPrompt: string;
  userPrompt: string;
  maxTokens?: number;
  temperature?: number;
  model?: string;
  continuationContext?: string;
  partIndex?: number;
  sectionTracker?: string;
}

export interface EdgeFunctionResponse {
  content: string;
  error?: string;
}

export interface StreamingEdgeFunctionResult {
  content: string;
  stopReason?: string;
}

export type ReviewStageStatus = 'analysing' | 'rewriting' | 'annotating' | 'finalising' | 'complete' | 'failed';

export interface ReviewProgress {
  status: ReviewStageStatus;
  stage: number;
  totalStages: number;
  message: string;
  percent: number;
  streamedChars?: number;
}

function isRetryable(status: number): boolean {
  return status === 429 || status === 502 || status === 503 || status === 504 || status >= 520;
}

async function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isStreamRetryable(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') return false;
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    if (msg.includes('stream stalled')) return true;
    if (msg.includes('timed out')) return true;
    if (msg.includes('network')) return true;
    if (msg.includes('fetch')) return true;
  }
  return false;
}

function isStreamRetryableStatus(status: number): boolean {
  return status === 429 || status === 502 || status === 503 || status === 504 || status >= 520;
}

async function executeStreamingRequest(
  request: EdgeFunctionRequest,
  onChunk: (text: string) => void,
  combinedSignal: AbortSignal,
): Promise<StreamingEdgeFunctionResult> {
  const response = await fetch(
    `${SUPABASE_URL}/functions/v1/generate-document`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'apikey': SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ ...request, stream: true }),
      signal: combinedSignal,
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
    const err = new Error(errorData.error || `Edge function failed with status ${response.status}`);
    (err as any).status = response.status;
    throw err;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No response body stream available');
  }

  const decoder = new TextDecoder();
  let accumulated = '';
  let buffer = '';
  let stopReason: string | undefined;

  let tabHiddenAt = 0;
  let hiddenAccumulated = 0;

  const onVisibilityChange = () => {
    if (document.hidden) {
      tabHiddenAt = Date.now();
    } else if (tabHiddenAt > 0) {
      hiddenAccumulated += Date.now() - tabHiddenAt;
      tabHiddenAt = 0;
    }
  };

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
  }

  const cleanupVisibility = () => {
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }
  };

  try {
  while (true) {
    const readPromise = reader.read();
    const stallStart = Date.now();
    const hiddenBefore = hiddenAccumulated;
    const stallPromise = new Promise<never>((_, reject) => {
      const checkStall = () => {
        const elapsed = Date.now() - stallStart;
        const hiddenDelta = hiddenAccumulated - hiddenBefore + (tabHiddenAt > 0 ? Date.now() - tabHiddenAt : 0);
        const activeElapsed = elapsed - hiddenDelta;
        if (activeElapsed >= STREAM_STALL_MS) {
          reject(new Error('Stream stalled: no data received for 120 seconds'));
        } else {
          stallTimer = setTimeout(checkStall, Math.min(10000, STREAM_STALL_MS - activeElapsed + 500));
        }
      };
      let stallTimer = setTimeout(checkStall, STREAM_STALL_MS);
      readPromise.then(() => clearTimeout(stallTimer), () => clearTimeout(stallTimer));
    });

    const { done, value } = await Promise.race([readPromise, stallPromise]);
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed.startsWith('data: ')) continue;

      const jsonStr = trimmed.slice(6);
      try {
        const parsed = JSON.parse(jsonStr);

        if (parsed.error) {
          throw new Error(parsed.error);
        }

        if (parsed.done) {
          stopReason = parsed.stop_reason;
          return { content: accumulated, stopReason };
        }

        if (parsed.delta) {
          accumulated += parsed.delta;
          onChunk(parsed.delta);
        }
      } catch (parseErr) {
        if (parseErr instanceof SyntaxError) continue;
        throw parseErr;
      }
    }
  }

  if (buffer.trim()) {
    const remainingLines = buffer.split('\n');
    for (const line of remainingLines) {
      const trimmed = line.trim();
      if (!trimmed.startsWith('data: ')) continue;
      try {
        const parsed = JSON.parse(trimmed.slice(6));
        if (parsed.error) {
          throw new Error(parsed.error);
        }
        if (parsed.done) {
          stopReason = parsed.stop_reason;
        } else if (parsed.delta) {
          accumulated += parsed.delta;
          onChunk(parsed.delta);
        }
      } catch (parseErr) {
        if (parseErr instanceof SyntaxError) continue;
        throw parseErr;
      }
    }
  }

  return { content: accumulated, stopReason };
  } finally {
    cleanupVisibility();
  }
}

export interface StreamingOptions {
  onRetry?: (attempt: number) => void;
}

export async function callStreamingEdgeFunction(
  request: EdgeFunctionRequest,
  onChunk: (text: string) => void,
  signal?: AbortSignal,
  options?: StreamingOptions,
): Promise<StreamingEdgeFunctionResult> {
  let lastError: Error | undefined;

  for (let attempt = 0; attempt <= STREAM_MAX_RETRIES; attempt++) {
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    if (attempt > 0) {
      const backoff = STREAM_RETRY_BASE_MS * Math.pow(2, attempt - 1);
      options?.onRetry?.(attempt);
      await delay(backoff);
    }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), STREAM_TIMEOUT_MS);

    const combinedSignal = signal
      ? AbortSignal.any([controller.signal, signal])
      : controller.signal;

    try {
      const result = await executeStreamingRequest(request, onChunk, combinedSignal);
      clearTimeout(timeout);
      return result;
    } catch (err) {
      clearTimeout(timeout);

      if (signal?.aborted) throw err;

      const status = (err as any)?.status;
      const canRetry = attempt < STREAM_MAX_RETRIES &&
        (isStreamRetryable(err) || (status && isStreamRetryableStatus(status)));

      if (!canRetry) throw err;

      lastError = err instanceof Error ? err : new Error(String(err));
      console.warn(`Streaming attempt ${attempt + 1} failed, retrying:`, lastError.message);
    }
  }

  throw lastError || new Error('Streaming failed after retries');
}

export async function callGenerateEdgeFunction(
  request: EdgeFunctionRequest
): Promise<EdgeFunctionResponse> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    if (attempt > 0) {
      const backoff = RETRY_DELAY_MS * Math.pow(2, attempt - 1);
      await delay(backoff);
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/generate-document`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'apikey': SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ ...request, stream: false }),
          signal: controller.signal,
        }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `Status ${response.status}` }));
        const errorMsg = errorData.error || `Edge function failed with status ${response.status}`;

        if (isRetryable(response.status) && attempt < MAX_RETRIES) {
          lastError = new Error(errorMsg);
          continue;
        }

        throw new Error(errorMsg);
      }

      const data: EdgeFunctionResponse = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      return data;
    } catch (err) {
      clearTimeout(timeoutId);

      if (err instanceof DOMException && err.name === 'AbortError') {
        lastError = new Error('Request timed out. The document may be too large to process in one step.');
        if (attempt < MAX_RETRIES) continue;
        throw lastError;
      }

      if (err instanceof TypeError && err.message.includes('fetch') && attempt < MAX_RETRIES) {
        lastError = err;
        continue;
      }

      throw err;
    }
  }

  throw lastError || new Error('Failed after retries');
}
